import type { FieldOption, FileUploadDefinition, IntakeCategoryId, IntakeData, IntakeDomain, IntakeField, IntakeGuard, IntakeStep, RepeaterField } from "./types";
import type {
  EffectiveIntakeSchema,
  FormCatalogItem,
  FormCatalogKind,
  FormConfigDiagnostic,
  FormConfigVersion,
  FormRule,
  FormRuleCondition,
  FormRuleContext,
  FormRuleEffect,
  FormRuleFieldCondition,
  OneOrMany,
} from "./form-config/types";

export type {
  EffectiveIntakeSchema,
  FormCatalogItem,
  FormCatalogKind,
  FormConfigDiagnostic,
  FormConfigVersion,
  FormRule,
  FormRuleCondition,
  FormRuleContext,
  FormRuleEffect,
  FormRuleFieldCondition,
} from "./form-config/types";

import {
  CONTRACT_OPTION_CATALOG,
  DEFAULT_FORM_CONFIG,
  MEDICAL_CONTRACT_OPTIONS,
  STANDARD_CONTRACT_OPTION_RULE,
  STANDARD_CONTRACT_OPTIONS,
  STATIC_CONTRACT_CATALOG,
  contractOptionsFromIds,
  getContractOptionIdByValue,
  type ContractOptionId,
} from "./form-config/contracts";

export {
  CONTRACT_OPTION_CATALOG,
  CONTRACT_OPTION_IDS,
  DEFAULT_FORM_CONFIG,
  FORM_CATALOG,
  FORM_CONFIG_VERSION_ID,
  MEDICAL_CONTRACT_OPTIONS,
  STANDARD_CONTRACT_OPTIONS,
  getContractOptionIdByValue,
} from "./form-config/contracts";
export type { ContractOptionId } from "./form-config/contracts";

import { createFormCatalogFromSteps, mergeFormCatalog, withRuntimeCatalog } from "./form-config/catalog";
import { hashText, targetIdForField, targetIdForFieldOption, targetIdForGuard, targetIdForRepeater, targetIdForRepeaterField, targetIdForRepeaterFieldOption, targetIdForStep, targetIdForUpload } from "./form-config/ids";

export { createFormCatalogFromSteps, mergeFormCatalog, reconcileCatalogWithRuntime, withRuntimeCatalog } from "./form-config/catalog";
export { targetIdForField, targetIdForFieldOption, targetIdForGuard, targetIdForRepeater, targetIdForRepeaterField, targetIdForRepeaterFieldOption, targetIdForStep, targetIdForUpload } from "./form-config/ids";

import { dedupeDiagnostics, resolveRuleEffect, uniqueRules, validateFormConfig } from "./form-config/rules";

export { sanitizeRuleOptionIds, validateFormConfig } from "./form-config/rules";

export function getContractOptionsForDomain(domain: string, config: FormConfigVersion = DEFAULT_FORM_CONFIG): FieldOption[] {
  return getContractOptionsForContext({ categoryId: "", domain: domain as IntakeDomain }, config).options;
}

export function getDefaultContractTypeForContext(categoryId: IntakeCategoryId, domain: string, config: FormConfigVersion = DEFAULT_FORM_CONFIG): string {
  return getContractOptionsForContext({ categoryId, domain: domain as IntakeDomain }, config).defaultValue;
}

// 카테고리(또는 도메인) 별 업셀링 수수료율 기본값. 문항 설정의 defaultValue rule로 관리한다.
export function getDefaultUpsellRateForContext(context: FormRuleContext, config: FormConfigVersion = DEFAULT_FORM_CONFIG): string {
  const runtimeCatalog = mergeFormCatalog(STATIC_CONTRACT_CATALOG, config.catalog);
  const runtimeConfig = { ...config, catalog: runtimeCatalog };
  const resolved = resolveRuleEffect("field.contract.upsellCommissionRate", context, runtimeConfig, undefined, runtimeCatalog);
  return resolved.effect.defaultValue === undefined ? "" : String(resolved.effect.defaultValue);
}

// 상담 상품 기본 행의 옵션명을 관리하는 문항 설정 대상.
export const CONSULTATION_PRODUCT_TARGET_ID = "repeaterField.products.optionName";

// 카테고리별 상담 상품 옵션명 재정의. undefined는 설정 없음(카테고리 기본 문구 사용),
// 빈 문자열은 상담 상품을 넣지 않겠다는 뜻이다.
export function getConsultationProductNameOverride(context: FormRuleContext, config: FormConfigVersion = DEFAULT_FORM_CONFIG): string | undefined {
  const runtimeCatalog = mergeFormCatalog(STATIC_CONTRACT_CATALOG, config.catalog);
  const runtimeConfig = { ...config, catalog: runtimeCatalog };
  const resolved = resolveRuleEffect(CONSULTATION_PRODUCT_TARGET_ID, context, runtimeConfig, undefined, runtimeCatalog);
  return resolved.effect.defaultValue === undefined ? undefined : String(resolved.effect.defaultValue);
}

export function getContractOptionsForContext(context: FormRuleContext, config: FormConfigVersion = DEFAULT_FORM_CONFIG): { options: FieldOption[]; defaultValue: string; appliedRules: FormRule[]; diagnostics: FormConfigDiagnostic[] } {
  const runtimeCatalog = mergeFormCatalog(STATIC_CONTRACT_CATALOG, config.catalog);
  const runtimeConfig = { ...config, catalog: runtimeCatalog };
  const resolved = resolveRuleEffect("field.contract.contractType", context, runtimeConfig, undefined, runtimeCatalog);
  const allowedOptionIds = resolved.effect.allowedOptionIds?.length ? resolved.effect.allowedOptionIds : STANDARD_CONTRACT_OPTION_RULE;
  const diagnostics = [...resolved.diagnostics];
  const appliedRules = [...resolved.appliedRules];
  const baseOptions = contractOptionsFromIds(allowedOptionIds.filter((optionId) => {
    if (CONTRACT_OPTION_CATALOG[optionId as ContractOptionId]) return true;
    diagnostics.push({ level: "error", code: "unknown_option", targetId: optionId, message: `Unknown contract option id: ${optionId}` });
    return false;
  }) as ContractOptionId[]);
  const options = applyOptionRules(baseOptions, (option) => getContractOptionIdByValue(option.value) ?? targetIdForFieldOption({ path: "contract.contractType" }, option), "field.contract.contractType", resolved.effect, context, runtimeConfig, appliedRules, diagnostics, undefined);
  const optionValues = new Set(options.map((option) => option.value));
  const fallbackValue = options[0]?.value ?? "";
  const defaultValue = resolved.effect.defaultValue && optionValues.has(String(resolved.effect.defaultValue)) ? String(resolved.effect.defaultValue) : fallbackValue;
  if (resolved.effect.defaultValue && !optionValues.has(String(resolved.effect.defaultValue))) {
    diagnostics.push({ level: "error", code: "invalid_default", targetId: "field.contract.contractType", message: `Default contract type '${resolved.effect.defaultValue}' is not allowed by the effective option set.` });
  }
  return { options, defaultValue, appliedRules: uniqueRules(appliedRules), diagnostics: dedupeDiagnostics(diagnostics) };
}

export function applyFormConfigToSteps(steps: IntakeStep[], context: FormRuleContext, config: FormConfigVersion = DEFAULT_FORM_CONFIG, data?: IntakeData): EffectiveIntakeSchema {
  const runtimeConfig = withRuntimeCatalog(config, steps);
  const diagnostics = validateFormConfig(runtimeConfig);
  const appliedRules: FormRule[] = [];

  const nextSteps = renumberVisibleSurveySteps(steps
    .map((step) => applyStepRules(step, context, runtimeConfig, appliedRules, diagnostics, data))
    .filter((step): step is IntakeStep => Boolean(step)));

  return {
    steps: nextSteps,
    catalog: createFormCatalogFromSteps(nextSteps),
    configVersionId: runtimeConfig.versionId,
    schemaHash: createEffectiveSchemaHash(nextSteps, runtimeConfig.versionId, context, uniqueRules(appliedRules)),
    diagnostics: dedupeDiagnostics(diagnostics),
    appliedRules: uniqueRules(appliedRules),
  };
}

function renumberVisibleSurveySteps(steps: IntakeStep[]): IntakeStep[] {
  let surveyStepNumber = 0;
  return steps.map((step) => {
    if (step.id === "welcome") return step;
    surveyStepNumber += 1;
    return { ...step, eyebrow: `Step ${surveyStepNumber}` };
  });
}

// defaultHidden 대상은 rule이 명시적으로 visible: true를 줄 때만 노출된다.
function isEffectivelyHidden(target: { defaultHidden?: boolean }, effect: FormRuleEffect): boolean {
  if (effect.visible !== undefined) return !effect.visible;
  return Boolean(target.defaultHidden);
}

function applyStepRules(step: IntakeStep, context: FormRuleContext, config: FormConfigVersion, appliedRules: FormRule[], diagnostics: FormConfigDiagnostic[], data?: IntakeData): IntakeStep | null {
  const targetId = targetIdForStep(step);
  const resolved = resolveRuleEffect(targetId, context, config, data);
  diagnostics.push(...resolved.diagnostics);
  appliedRules.push(...resolved.appliedRules);
  if (isEffectivelyHidden(step, resolved.effect)) {
    if (resolved.effect.visible === false) diagnostics.push({ level: "warning", code: "hidden_step", targetId, message: `${step.title} step is hidden for this scenario.` });
    return null;
  }

  const patchedStep = applyCommonEffect(step, resolved.effect);
  return {
    ...patchedStep,
    fields: patchedStep.fields
      ?.map((field) => applyFieldRules(field, context, config, appliedRules, diagnostics, data))
      .filter((field): field is IntakeField => Boolean(field)),
    guards: patchedStep.guards
      ?.map((guard) => applyGuardRules(guard, context, config, appliedRules, diagnostics, data))
      .filter((guard): guard is IntakeGuard => Boolean(guard)),
    repeaters: patchedStep.repeaters
      ?.map((repeater) => applyRepeaterRules(repeater, context, config, appliedRules, diagnostics, data))
      .filter((repeater): repeater is RepeaterField => Boolean(repeater)),
    fileUploads: patchedStep.fileUploads
      ?.map((upload) => applyUploadRules(upload, context, config, appliedRules, diagnostics, data))
      .filter((upload): upload is FileUploadDefinition => Boolean(upload)),
  };
}

function applyGuardRules(guard: IntakeGuard, context: FormRuleContext, config: FormConfigVersion, appliedRules: FormRule[], diagnostics: FormConfigDiagnostic[], data?: IntakeData): IntakeGuard | null {
  const targetId = targetIdForGuard(guard);
  const resolved = resolveRuleEffect(targetId, context, config, data);
  diagnostics.push(...resolved.diagnostics);
  appliedRules.push(...resolved.appliedRules);
  const isHidden = resolved.effect.visible !== undefined
    ? !resolved.effect.visible
    : guard.defaultVisibleDomains
      ? !guard.defaultVisibleDomains.some((domain) => domain === context.domain)
      : Boolean(guard.defaultHidden);
  if (isHidden) return null;
  return applyCommonEffect(guard, resolved.effect);
}

function applyFieldRules(field: IntakeField, context: FormRuleContext, config: FormConfigVersion, appliedRules: FormRule[], diagnostics: FormConfigDiagnostic[], data?: IntakeData): IntakeField | null {
  const targetId = targetIdForField(field);
  const resolved = resolveRuleEffect(targetId, context, config, data);
  diagnostics.push(...resolved.diagnostics);
  appliedRules.push(...resolved.appliedRules);
  if (isEffectivelyHidden(field, resolved.effect)) return null;
  const patchedField = applyCommonEffect(field, resolved.effect);
  if (!patchedField.options?.length && !resolved.effect.allowedOptionIds?.length) return patchedField;
  const baseOptions = expandOptionsFromCatalog(patchedField.options ?? [], targetId, resolved.effect.allowedOptionIds, config.catalog);
  const options = applyOptionRules(baseOptions, (option) => targetIdForFieldOption(patchedField, option), targetId, resolved.effect, context, config, appliedRules, diagnostics, data);
  return normalizeFieldDefault({ ...patchedField, options }, diagnostics, targetId);
}

function applyRepeaterRules(repeater: RepeaterField, context: FormRuleContext, config: FormConfigVersion, appliedRules: FormRule[], diagnostics: FormConfigDiagnostic[], data?: IntakeData): RepeaterField | null {
  const targetId = targetIdForRepeater(repeater);
  const resolved = resolveRuleEffect(targetId, context, config, data);
  diagnostics.push(...resolved.diagnostics);
  appliedRules.push(...resolved.appliedRules);
  if (resolved.effect.visible === false) return null;
  const patchedRepeater = applyCommonEffect(repeater, resolved.effect);
  return {
    ...patchedRepeater,
    fields: patchedRepeater.fields
      .map((field) => applyRepeaterFieldRules(patchedRepeater, field, context, config, appliedRules, diagnostics, data))
      .filter((field): field is RepeaterField["fields"][number] => Boolean(field)),
  };
}

function applyRepeaterFieldRules(repeater: RepeaterField, field: RepeaterField["fields"][number], context: FormRuleContext, config: FormConfigVersion, appliedRules: FormRule[], diagnostics: FormConfigDiagnostic[], data?: IntakeData): RepeaterField["fields"][number] | null {
  const targetId = targetIdForRepeaterField(repeater, field);
  const resolved = resolveRuleEffect(targetId, context, config, data);
  diagnostics.push(...resolved.diagnostics);
  appliedRules.push(...resolved.appliedRules);
  if (isEffectivelyHidden(field, resolved.effect)) return null;
  const patchedField = applyCommonEffect(field, resolved.effect);
  if (!patchedField.options?.length && !resolved.effect.allowedOptionIds?.length) return patchedField;
  const baseOptions = expandOptionsFromCatalog(patchedField.options ?? [], targetId, resolved.effect.allowedOptionIds, config.catalog);
  const options = applyOptionRules(baseOptions, (option) => targetIdForRepeaterFieldOption(repeater, patchedField, option), targetId, resolved.effect, context, config, appliedRules, diagnostics, data);
  return normalizeFieldDefault({ ...patchedField, options }, diagnostics, targetId);
}

function applyUploadRules(upload: FileUploadDefinition, context: FormRuleContext, config: FormConfigVersion, appliedRules: FormRule[], diagnostics: FormConfigDiagnostic[], data?: IntakeData): FileUploadDefinition | null {
  const targetId = targetIdForUpload(upload);
  const resolved = resolveRuleEffect(targetId, context, config, data);
  diagnostics.push(...resolved.diagnostics);
  appliedRules.push(...resolved.appliedRules);
  if (isEffectivelyHidden(upload, resolved.effect)) return null;
  return applyCommonEffect(upload, resolved.effect);
}

function expandOptionsFromCatalog(options: FieldOption[], parentTargetId: string, allowedOptionIds: string[] | undefined, catalog: FormCatalogItem[]): FieldOption[] {
  if (!allowedOptionIds?.length) return options;
  const existingIds = new Set(options.map((option) => option.id).filter(Boolean));
  const existingValues = new Set(options.map((option) => option.value));
  const optionItems = new Map(catalog.filter((item) => item.kind === "option" && item.parentId === parentTargetId).map((item) => [item.id, item]));
  const additions = allowedOptionIds.flatMap((optionId) => {
    if (existingIds.has(optionId)) return [];
    const item = optionItems.get(optionId);
    if (!item?.optionValue || existingValues.has(item.optionValue)) return [];
    return [{ id: item.id, label: item.label, value: item.optionValue, description: item.description } satisfies FieldOption];
  });
  return [...options, ...additions];
}

function applyOptionRules(options: FieldOption[], getOptionTargetId: (option: FieldOption) => string, parentTargetId: string, parentEffect: FormRuleEffect, context: FormRuleContext, config: FormConfigVersion, appliedRules: FormRule[], diagnostics: FormConfigDiagnostic[], data?: IntakeData): FieldOption[] {
  const allowedOptionIds = parentEffect.allowedOptionIds?.length ? new Set(parentEffect.allowedOptionIds) : null;
  const effectiveAllowedIds = allowedOptionIds && options.some((option) => allowedOptionIds.has(getOptionTargetId(option))) ? allowedOptionIds : null;
  if (allowedOptionIds && !effectiveAllowedIds) {
    diagnostics.push({ level: "warning", code: "stale_allowed_options", targetId: parentTargetId, message: `Allowed option ids for '${parentTargetId}' matched no current options; showing all options instead.` });
  }
  return options
    .map((option) => {
      const optionTargetId = getOptionTargetId(option);
      if (effectiveAllowedIds && !effectiveAllowedIds.has(optionTargetId)) return null;
      const resolved = resolveRuleEffect(optionTargetId, context, config, data);
      diagnostics.push(...resolved.diagnostics);
      appliedRules.push(...resolved.appliedRules);
      if (resolved.effect.visible === false) return null;
      return applyOptionEffect({ ...option, id: optionTargetId }, resolved.effect);
    })
    .filter((option): option is FieldOption => Boolean(option))
    .map((option, index, visibleOptions) => {
      if (!option.id) return option;
      const desiredOrder = parentEffect.allowedOptionIds;
      if (!desiredOrder?.length) return option;
      return { ...option, _sort: desiredOrder.indexOf(option.id) === -1 ? visibleOptions.length + index : desiredOrder.indexOf(option.id) } as FieldOption & { _sort: number };
    })
    .sort((left, right) => ((left as FieldOption & { _sort?: number })._sort ?? 0) - ((right as FieldOption & { _sort?: number })._sort ?? 0))
    .map((option) => {
      const { _sort: _unused, ...cleanOption } = option as FieldOption & { _sort?: number };
      return cleanOption;
    });
}

function normalizeFieldDefault<T extends IntakeField | RepeaterField["fields"][number]>(field: T, diagnostics: FormConfigDiagnostic[], targetId: string): T {
  // textarea의 options는 선택지 검증이 아니라 전용 UI(예: 단독 혜택 카드)를 구성하는 메타데이터다.
  if (!field.options?.length || field.defaultValue === undefined || field.defaultValue === "" || !["radio", "select", "multiCheckbox"].includes(field.type)) return field;
  const allowedValues = new Set(field.options.map((option) => option.value));
  if (field.type === "multiCheckbox") {
    const nextDefault = String(field.defaultValue)
      .split("\n")
      .map((value) => value.trim())
      .filter((value) => value && allowedValues.has(value))
      .join("\n");
    if (nextDefault !== String(field.defaultValue)) {
      diagnostics.push({ level: "warning", code: "default_filtered", targetId, message: `Default values for '${field.label}' were filtered to visible options.` });
      return { ...field, defaultValue: nextDefault };
    }
    return field;
  }
  if (!allowedValues.has(String(field.defaultValue))) {
    const fallback = field.options[0]?.value ?? "";
    diagnostics.push({ level: "error", code: "invalid_default", targetId, message: `Default value '${field.defaultValue}' is not allowed for '${field.label}'.` });
    return { ...field, defaultValue: fallback };
  }
  return field;
}

function applyCommonEffect<T extends { label?: string; title?: string; description?: string; required?: boolean; placeholder?: string; helperText?: string; checkboxText?: string; defaultValue?: string | number | boolean; addLabel?: string; minItems?: number }>(target: T, effect: FormRuleEffect): T {
  const next = { ...target } as T & {
    label?: string;
    title?: string;
    description?: string;
    required?: boolean;
    placeholder?: string;
    helperText?: string;
    checkboxText?: string;
    defaultValue?: string | number | boolean;
    addLabel?: string;
    minItems?: number;
  };
  if (effect.label !== undefined) {
    if ("label" in target) next.label = effect.label;
    if ("title" in target) next.title = effect.label;
  }
  if (effect.title !== undefined) next.title = effect.title;
  if (effect.description !== undefined) next.description = effect.description;
  if (effect.required !== undefined) next.required = effect.required;
  if (effect.placeholder !== undefined) next.placeholder = effect.placeholder;
  if (effect.helperText !== undefined) next.helperText = effect.helperText;
  if (effect.checkboxText !== undefined) next.checkboxText = effect.checkboxText;
  if (effect.defaultValue !== undefined) next.defaultValue = effect.defaultValue;
  if (effect.addLabel !== undefined) next.addLabel = effect.addLabel;
  if (effect.minItems !== undefined) next.minItems = effect.minItems;
  return next;
}

function applyOptionEffect(option: FieldOption, effect: FormRuleEffect): FieldOption {
  return {
    ...option,
    label: effect.label ?? option.label,
    description: effect.description ?? effect.helperText ?? option.description,
  };
}

export function createEffectiveSchemaHash(steps: IntakeStep[], versionId: string, context: FormRuleContext, appliedRules: FormRule[]): string {
  const stablePayload = JSON.stringify({
    versionId,
    context,
    appliedRules: appliedRules.map((rule) => ({ id: rule.id, targetId: rule.targetId, effect: rule.effect })),
    steps: steps.map((step) => ({
      id: step.id,
      title: step.title,
      description: step.description,
      fields: step.fields?.map((field) => ({ id: field.id, label: field.label, required: Boolean(field.required), defaultValue: field.defaultValue, helperText: field.helperText, checkboxText: field.checkboxText, placeholder: field.placeholder, options: field.options?.map((option) => ({ id: option.id, value: option.value, label: option.label, description: option.description })) })),
      guards: step.guards?.map((guard) => ({ id: guard.id, label: guard.label, description: guard.description })),
      repeaters: step.repeaters?.map((repeater) => ({ id: repeater.id, label: repeater.label, fields: repeater.fields.map((field) => ({ id: field.id, label: field.label, required: Boolean(field.required), defaultValue: field.defaultValue, helperText: field.helperText, checkboxText: field.checkboxText, placeholder: field.placeholder })) })),
      uploads: step.fileUploads?.map((upload) => ({ id: upload.id, label: upload.label, required: Boolean(upload.required), description: upload.description })),
    })),
  });
  let hash = 2166136261;
  for (let index = 0; index < stablePayload.length; index += 1) {
    hash ^= stablePayload.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `schema_${(hash >>> 0).toString(16)}`;
}
